import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useMemo } from 'react';
import { useMicroTrendsTimePeriodStore } from './MicroTrendStore';
import { useCategoryEntity } from 'src/utils/Hooks/reduxSelectors';

export interface MicroTrendsRequestBody {
  trendIds: number[];
  categoryIds: number[];
  subcategoryIds?: number[];
  retailerId: number;
  query?: string;
}

export interface MicroTrend {
  retailerId: number;
  categoryId: number;
  subcategoryId: number;
  segment: string;
  numSkus: number;
  brandName: string;
  startWeekId: number;
  endWeekId: number;
  numWeeks: number;
  summary: string;
  trendId: string;
  tagName: string;
  tagValue: string;
  type: number;
}

export interface MicroTrendsResponse {
  isSuccess: boolean;
  trendGroups: { trendGroupId: string; trends: MicroTrend[] }[];
  message?: string;
}

const apiGetMicroTrends = async (requestBody: MicroTrendsRequestBody): Promise<MicroTrendsResponse> => {
  const response = await axios.post<MicroTrendsResponse>('/api/atlas/microtrends/GetMicrotrends', requestBody);
  return response.data;
};

export interface UseMicroTrendsListDataParams {
  trendIds?: number[];
  categoryIds?: number[];
  subcategoryIds?: number[];
  retailerId: number;
  query?: string;
  enabled?: boolean;
  staleTime?: number;
  onError?: (error: any) => void;
}

// Custom hook
export const useMicroTrendsListData = ({
  trendIds = [],
  categoryIds = [],
  subcategoryIds = [],
  retailerId,
  query,
  enabled = true,
  staleTime = 60 * 60 * 1000,
  onError
}: UseMicroTrendsListDataParams) => {
  const timePeriod = useMicroTrendsTimePeriodStore((state) => state.timePeriod);
  const { startWeek, endWeek } = timePeriod;
  const { isAllCategoriesEntity } = useCategoryEntity();

  const requestBody: MicroTrendsRequestBody = {
    trendIds,
    categoryIds,
    ...(subcategoryIds.length > 0 && { subcategoryIds }),
    retailerId,
    ...(query && { query })
  };

  const queryResult = useQuery({
    queryKey: ['microtrends', requestBody, startWeek, endWeek],
    queryFn: () => apiGetMicroTrends(requestBody),
    enabled:
      enabled && (categoryIds.length > 0 || subcategoryIds.length > 0 || (isAllCategoriesEntity && !!retailerId)),
    staleTime,
    refetchOnWindowFocus: false,
    retry: 1
  });

  // Handle errors
  if (queryResult.isError && onError) {
    onError(queryResult.error);
  }

  const filteredTrendGroups = useMemo(() => {
    return queryResult.data?.trendGroups.filter((trendGroup) => {
      return trendGroup.trends.some((trend) => {
        return trend.startWeekId <= endWeek && trend.endWeekId >= startWeek;
      });
    });
  }, [queryResult.data?.trendGroups, startWeek, endWeek]);

  return {
    data: queryResult.data?.trendGroups as { trendGroupId: string; trends: MicroTrend[] }[],
    filteredTrendGroups,
    isLoading: queryResult.isLoading,
    error: queryResult.error,
    refetch: queryResult.refetch
  };
};

export default useMicroTrendsListData;
